import { catchError, map, switchMap } from 'rxjs/operators';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ModalOverlayRef } from '../../modal/modal-overlay-ref';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  Contractor,
  ContractorsList,
  Monitoring,
  TransService,
  Payment,
  TransRequest,
  LoadDetails,
  TransSpotSchema,
} from '../../core/services/trans.service';
import { tap } from 'rxjs/operators';
import { EMPTY, Observable, of, Subscription } from 'rxjs';
import { Group, SharedProject } from '../../core/types';
import { PdfFromProjectService } from '../../core/services/pdf-from-project.service';
import { Project } from '../../core/objects/project';
import { ProjectService } from '../../core/services/project.service';
import { AuthorizationService } from '../../authorization/services/authorization.service';

interface FreightFormValue {
  publish: boolean;
  capacity: number;
  ldm: number;
  vehicle_size: string[];
  requirements: {
    is_ftl: boolean;
    required_truck_bodies: string[];
  };
  spots: SpotFormValue[];
  payment: {
    days: number;
    price: {
      type: string;
      value: number;
      currency: string;
    };
  };
  monitoring: {
    required: boolean;
  };
}

interface SpotFormValue {
  spot_order: number;
  place: {
    address: {
      country: string;
      locality: string;
      postal_code: string;
    };
  };
  operations: OperationFormValue[];
}

interface OperationLoads {
  isChecked: boolean;
  load: Group;
  numberOfLoads: number;
}

interface OperationFormValue {
  type: string;
  loads: OperationLoads[];
  timespans: {
    begin: string;
    end: string;
  };
}

@Component({
    selector: 'gl-send-to-trans',
    templateUrl: './send-to-trans.component.html',
    styleUrls: ['./send-to-trans.component.scss'],
    standalone: false
})
export class SendToTransComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  public isContractorList = false;
  public isOfferMenu = true;
  public isContractorListEmpty = false;
  public contractorListData: any[] = [];
  public isTransId = false;
  private project: Project;
  private currentWorkSpaceIndex: number;
  public isTransForm = false;
  public isOrder = false;
  public isError = false;
  public isNoAttachment = false;
  public isSuccess = false;
  private subscriptions: Subscription[] = [];
  private unit = 'kg';

  constructor(
    public modalRef: ModalOverlayRef,
    private fb: UntypedFormBuilder,
    private transService: TransService,
    private pdfFromProjectService: PdfFromProjectService,
    private projectService: ProjectService,
    private authorizationService: AuthorizationService,
  ) {
    this.getCompanyInfo();
  }

  private getCompanyInfo(): void {
    this.transService
      .getCompanyInfo(false)
      .pipe(
        catchError((error) => {
          console.error('Error in getCompanyInfo', error);
          this.isTransId = false;
          this.authorizationService.redirectToTransLogin();
          return EMPTY;
        }),
      )
      .subscribe((): void => {
        this.isTransId = true;
      });
  }

  private remapContractorsList(data: ContractorsList): any {
    const modifiedData = data.partners.map((item: Contractor) => {
      return {
        ...item,
        name: `${item.company.name}`,
        id: `${item.id}`,
      };
    });
    return modifiedData;
  }

  private getContractorsList(page = 1, allData: any[] = []): Observable<any> {
    return this.transService.getContractorsList(false, page).pipe(
      catchError((error) => {
        console.error('Error in getContractorsList', error);
        this.resetState();
        this.isError = true;
        return EMPTY;
      }),
      switchMap((data: ContractorsList) => {
        allData.push(...this.remapContractorsList(data));
        if (page < data.page_count) {
          return this.getContractorsList(page + 1, allData);
        } else {
          this.contractorListData = allData;
          this.isContractorListEmpty = !allData.length;
          return of(allData);
        }
      }),
    );
  }

  private prepareAllLoadsToSend(data: FreightFormValue): LoadDetails[] {
    const allLoadsToSend: LoadDetails[] = [];
    data.spots.forEach((spot: SpotFormValue) => {
      spot.operations.forEach((operation: OperationFormValue) => {
        const isOperationLoading = operation.type === 'loading';
        if (isOperationLoading) {
          allLoadsToSend.push(...this.convertLoadsToAPIFormat(operation.loads, false));
        }
      });
    });
    return allLoadsToSend;
  }

  private getLoadingSide(): string[] {
    const loadingSide: string =
      this.project.workSpaces[this.currentWorkSpaceIndex].cargoSpaces[0].trailerLoadingSide;
    const loadingSide2: string =
      this.project.workSpaces[this.currentWorkSpaceIndex].cargoSpaces[1]?.trailerLoadingSide;
    const trailerRet = loadingSide === 'left' || loadingSide === 'right' ? 'side' : loadingSide;
    const semitRet = loadingSide2 === 'left' || loadingSide2 === 'right' ? 'trailer' : loadingSide2;
    return semitRet ? [trailerRet, semitRet] : [trailerRet];
  }

  private createFreightData(
    pdf: Blob,
    data: FreightFormValue,
    carrierId: number = undefined,
  ): TransRequest {
    const allLoadsToSend: LoadDetails[] = this.prepareAllLoadsToSend(data);
    const formatDate = (dateStr: string): string => {
      const date = new Date(dateStr);
      const isoString = date.toISOString();
      return isoString.replace(/\.\d{3}Z$/, '+0000');
    };
    const loadingSide: string[] = this.getLoadingSide();
    const newFreightData: TransRequest = {
      publish: data.publish,
      capacity: parseFloat(data.capacity as unknown as string),
      loading_meters: parseFloat(data.ldm as unknown as string),
      vehicle_size: data.vehicle_size,
      loads: allLoadsToSend,
      requirements: {
        is_ftl: data.requirements.is_ftl,
        required_truck_bodies: Array.isArray(data.requirements.required_truck_bodies)
          ? data.requirements.required_truck_bodies
          : [],
        required_ways_of_loading: loadingSide,
      },
      carrier_id: carrierId,
      spots: data.spots.map((spot, index) => ({
        spot_order: index + 1,
        place: {
          address: {
            country: spot.place.address.country,
            postal_code: spot.place.address.postal_code,
            locality: spot.place.address.locality,
          },
        },
        operations: spot.operations.map((operation, opIndex) => ({
          operation_order: opIndex + 1,
          type: operation.type as 'loading' | 'unloading',
          loads: this.convertLoadsToAPIFormat(operation.loads, true),
          timespans: {
            begin: formatDate(operation.timespans.begin),
            end: operation.timespans.end
              ? formatDate(operation.timespans.end)
              : formatDate(operation.timespans.begin),
          },
        })),
      })),
      pdf: pdf,
    };
    return this.splitSpotsByOperations(newFreightData);
  }

  private createOrderData(
    pdf: Blob,
    data: FreightFormValue,
    carrierId: number = undefined,
  ): TransRequest {
    const allLoadsToSend: LoadDetails[] = this.prepareAllLoadsToSend(data);
    const formatDate = (dateStr: string): string => {
      const date = new Date(dateStr);
      const isoString = date.toISOString();
      return isoString.replace(/\.\d{3}Z$/, '+0000');
    };
    const payment: Payment = {
      days: data.payment.days,
      price: {
        type: data.payment.price.type as unknown as 'km' | 'route',
        value: data.payment.price.value,
        currency: data.payment.price.currency,
      },
    };
    const monitoring: Monitoring = { required: data.monitoring.required };
    const loadingSide: string[] = this.getLoadingSide();
    const newOrderData: TransRequest = {
      publish: data.publish,
      capacity: parseFloat(data.capacity as unknown as string),
      loads: allLoadsToSend,
      loading_meters: parseFloat(data.ldm as unknown as string),
      vehicle_size: data.vehicle_size,
      requirements: {
        is_ftl: data.requirements.is_ftl,
        required_truck_bodies: Array.isArray(data.requirements.required_truck_bodies)
          ? data.requirements.required_truck_bodies
          : [],
        required_ways_of_loading: loadingSide,
      },
      carrier_id: carrierId,
      spots: data.spots.map((spot, index: number) => ({
        spot_order: index + 1,
        place: {
          address: {
            country: spot.place.address.country,
            postal_code: spot.place.address.postal_code,
            locality: spot.place.address.locality,
          },
        },
        operations: spot.operations.map((operation: OperationFormValue, opIndex: number) => ({
          operation_order: opIndex + 1,
          type: operation.type as 'loading' | 'unloading',
          loads: this.convertLoadsToAPIFormat(operation.loads, false),
          timespans: {
            begin: formatDate(operation.timespans.begin),
            end: operation.timespans.end
              ? formatDate(operation.timespans.end)
              : formatDate(operation.timespans.begin),
          },
        })),
      })),
      pdf: pdf,
      payment: payment,
      monitoring: monitoring,
    };
    return this.splitSpotsByOperations(newOrderData);
  }

  private splitSpotsByOperations(requestData: TransRequest): TransRequest {
    const newSpots: TransSpotSchema[] = [];
    requestData.spots.forEach((spot) => {
      if (spot.operations.length > 1) {
        spot.operations.forEach((operation) => {
          const newSpot: TransSpotSchema = {
            ...spot,
            operations: [{ ...operation, operation_order: 1 }],
            spot_order: newSpots.length + 1,
          };
          newSpots.push(newSpot);
        });
      } else {
        newSpots.push({ ...spot, spot_order: newSpots.length + 1 });
      }
    });
    return { ...requestData, spots: newSpots };
  }

  private convertLoadTypeToApiFormat(LoadType: number): string {
    switch (LoadType) {
      case 0:
        return 'box';
      case 1:
        return 'barrel';
      case 2:
        return 'roll';
      default:
        return 'other';
    }
  }

  private convertLoadsToAPIFormat(loads: OperationLoads[], isOperation = false): LoadDetails[] {
    if (loads.length === 0) {
      return [];
    }
    return loads
      .filter((load1: OperationLoads) => load1.isChecked)
      .map((loadLoad: OperationLoads): LoadDetails => {
        const load: Group = loadLoad.load;
        const fullLoad: LoadDetails = {
          load_id: load.id.toString(10),
          name: load.name,
          type_of_load: this.convertLoadTypeToApiFormat(load.loadType),
          weight: { value: load.weight, unit_code: this.unit },
          height: load.dimensions.height,
          width: load.dimensions.width,
          length: load.dimensions.length,
          amount: loadLoad.numberOfLoads,
          is_stackable: load.stacking,
        };
        const operationLoad: LoadDetails = {
          load_id: load.id.toString(10),
          weight: { value: load.weight, unit_code: this.unit },
        };
        return isOperation ? operationLoad : fullLoad;
      });
  }

  private changeErrorMessage(error: any): void {
    const isNoAttachment =
      typeof error.error === 'string' && error.error.includes('pdf is not attached');
    if (isNoAttachment) {
      this.isNoAttachment = true;
    } else {
      this.isError = true;
    }
  }

  public transFormSubmit(data: UntypedFormGroup): void {
    if (!this.isOrder) {
      this.subscriptions.push(
        this.createNewFreight(data as unknown as FreightFormValue)
          .pipe(
            catchError((error) => {
              console.error('Error in createNewFreight', error);
              this.changeErrorMessage(error);
              this.resetState();
              return EMPTY;
            }),
          )
          .subscribe((): void => {
            this.isSuccess = true;
            this.resetState();
          }),
      );
    } else {
      this.subscriptions.push(
        this.createNewOrder(data as unknown as FreightFormValue)
          .pipe(
            catchError((error) => {
              console.error('Error in createNewOrder', error);
              this.changeErrorMessage(error);
              this.resetState();
              return EMPTY;
            }),
          )
          .subscribe((): void => {
            this.isSuccess = true;
            this.resetState();
          }),
      );
    }
  }

  private resetState(): void {
    this.isTransForm = false;
    this.isContractorList = false;
  }

  public startNewOrder(): void {
    this.isOrder = true;
    this.isOfferMenu = false;
    this.isTransForm = true;
    this.isContractorList = false;
  }

  public createNewOrder(data: FreightFormValue): Observable<any> {
    this.isError = false;
    this.isNoAttachment = false;
    return this.getProjectData().pipe(
      switchMap(() => this.downloadPdf()),
      map((pdf: Blob) => {
        const carrierId: number = this.form.value.radioOption as number;
        return this.createOrderData(pdf, data, carrierId);
      }),
      switchMap((orderData: TransRequest) => this.transService.postNewOrder(orderData, false)),
    );
  }

  public startNewFreight(): void {
    this.isTransForm = true;
    this.isOrder = false;
    this.isOfferMenu = false;
  }

  private createNewFreight(data: FreightFormValue): Observable<any> {
    this.isError = false;
    this.isNoAttachment = false;
    return this.getProjectData().pipe(
      switchMap(() => this.downloadPdf()),
      map((pdf: Blob) => {
        return this.createFreightData(pdf, data);
      }),
      switchMap((orderData: TransRequest) => {
        return this.transService.postNewFreightOffer(orderData, false);
      }),
    );
  }

  public openContractorList(): void {
    this.isOrder = true;
    this.getContractorsList().subscribe((): void => {
      this.isContractorList = true;
      this.isOfferMenu = false;
    });
  }

  private getCurrentWorkSpaceIndex(): void {
    this.projectService.currentWorkSpaceIndex.subscribe((index: number) => {
      this.currentWorkSpaceIndex = index;
    });
  }

  public ngOnInit(): void {
    this.getCurrentWorkSpaceIndex();
    this.generateForm();
  }

  private generateForm(): void {
    this.form = this.fb.group({
      radioOption: [''],
    });
  }

  private getProjectData(): Observable<Project> {
    return this.projectService.project.pipe(
      tap((project: Project) => {
        this.project = project;
        this.unit = project.options.unit === 'cm' || project.options.unit === 'mm' ? 'kg' : 'lb';
      }),
    );
  }

  public downloadPdf(): Observable<Blob> {
    const projectData: SharedProject & { description: string } = {
      description: 'description',
      project: this.project,
      name: 'name',
      note: 'note',
    };
     
    return this.pdfFromProjectService
      ?.generateSharePDF(
        projectData,
        this.currentWorkSpaceIndex,
        {
          imageSrc: null,
          currentWorkSpace: this.currentWorkSpaceIndex,
        },
        true,
      )
      .pipe(
        map((pdf: Blob) => {
          return pdf;
        }),
      );
  }

  public onClose(): void {
    this.modalRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
